/**
 * Set your own face.
 *
 * Client-side downscale before upload is load-bearing, not polish: there's no
 * CDN in front of Replit Object Storage, so every avatar byte flows through the
 * Node process on every directory page. A 4 MB phone photo × 150 members is a
 * real cost; 512px is plenty for a 96px circle.
 */
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { toast } from '../lib/toast';
import { Avatar } from './Avatar';
import { Button } from './ui';

const MAX_DIM = 512;

/** Square-crop and downscale to a JPEG, in the browser. */
async function downscale(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = MAX_DIM;
  canvas.height = MAX_DIM;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(
    bitmap,
    (bitmap.width - side) / 2, // centre crop
    (bitmap.height - side) / 2,
    side,
    side,
    0,
    0,
    MAX_DIM,
    MAX_DIM,
  );
  return new Promise<Blob>((resolve) =>
    canvas.toBlob((b) => resolve(b ?? file), 'image/jpeg', 0.85),
  );
}

export function AvatarUpload() {
  const { user, refreshUser } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  if (!user) return null;

  async function onPick(file: File) {
    setBusy(true);
    try {
      const blob = await downscale(file);
      const form = new FormData();
      form.append('type', 'avatar');
      form.append('file', new File([blob], 'avatar.jpg', { type: 'image/jpeg' }));
      const result = await api.upload<{ url: string }>('/media', form);
      setPreview(result.url);
      toast.success('Your photo is set.');
      await refreshUser();
      void qc.invalidateQueries({ queryKey: ['members'] });
    } catch (e) {
      toast.error(
        e instanceof ApiError ? e.message : 'That photo could not be uploaded.',
      );
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div className="flex items-center gap-4">
      <Avatar
        firstName={user.firstName}
        lastName={user.lastName}
        avatarUrl={preview ?? user.avatarUrl}
        size="xl"
      />
      <div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onPick(file);
          }}
        />
        <Button variant="ghost" loading={busy} onClick={() => fileRef.current?.click()}>
          {user.avatarUrl || preview ? 'Change photo' : 'Add a photo'}
        </Button>
        <p className="mt-1 text-xs text-cream-300/45">
          Your face appears in the directory, on attendee lists, and beside your tasting notes.
        </p>
      </div>
    </div>
  );
}
